import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ThemeService } from '../../../../core/services/theme';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ProductDetail,
  ProductType,
  StatusUtils,
} from '../../models/product.interface';
import { ProductService } from '../../services/product.service';

@Component({
  selector: 'app-product-audit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    TextareaModule,
    TranslateModule,
  ],
  templateUrl: './product-audit.component.html',
  styleUrl: './product-audit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductAuditComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly productService = inject(ProductService);
  private readonly translateService = inject(TranslateService);
  protected readonly themeService = inject(ThemeService);

  productDetail = signal<ProductDetail | null>(null);
  isLoading = signal(true);
  error = signal<string | null>(null);
  productType = signal<ProductType>('ordinary');

  // Modal state
  showModal = signal(false);
  modalTitle = signal('');
  modalAction = signal<'pass' | 'turnDown'>('pass');
  auditComment = signal('');
  maxCommentLength = 200;

  ngOnInit(): void {
    this.setupRouteListener();
    this.loadProductDetail();
  }

  private setupRouteListener(): void {
    this.route.params.subscribe((params) => {
      const type = params['type'] as ProductType;
      if (type === 'package' || type === 'ordinary') {
        this.productType.set(type);
      } else {
        this.productType.set('ordinary');
      }
    });
  }

  loadProductDetail(): void {
    this.route.params.subscribe((params) => {
      const productId = params['product_id'];
      this.isLoading.set(true);
      this.error.set(null);

      this.productService
        .getProductDetail(parseInt(productId), this.productType())
        .subscribe({
          next: (product) => {
            this.productDetail.set(product);
            this.isLoading.set(false);
          },
          error: (error) => {
            console.error('Error loading product detail:', error);
            this.error.set(
              error.message ||
                this.translateService.instant(
                  'goods.product_review.detail.messages.failed_to_load'
                )
            );
            this.isLoading.set(false);
          },
        });
    });
  }

  onPass(): void {
    this.modalTitle.set(
      this.translateService.instant(
        'goods.product_review.detail.messages.approved'
      )
    );
    this.modalAction.set('pass');
    this.auditComment.set('');
    this.showModal.set(true);
  }

  onTurnDown(): void {
    this.modalTitle.set(
      this.translateService.instant(
        'goods.product_review.detail.messages.rejected'
      )
    );
    this.modalAction.set('turnDown');
    this.auditComment.set('');
    this.showModal.set(true);
  }

  onReturn(): void {
    window.history.back();
  }

  onModalCancel(): void {
    this.showModal.set(false);
    this.auditComment.set('');
  }

  onModalConfirm(): void {
    const productId = this.productDetail()?.id;
    if (!productId) return;

    this.productService
      .submitAudit(productId, this.modalAction(), this.auditComment())
      .subscribe({
        next: (success) => {
          if (success) {
            this.showModal.set(false);
            this.auditComment.set('');
            this.onReturn();
          }
        },
        error: (error) => {
          console.error('Error submitting audit:', error);
          // You might want to show an error message to the user here
          // For now, we'll just log the error
        },
      });
  }

  onCommentChange(event: any): void {
    const value = event.target.value;
    if (value.length <= this.maxCommentLength) {
      this.auditComment.set(value);
    }
  }

  getCommentLength(): number {
    return this.auditComment().length;
  }

  getStatusLabel(status?: number): string {
    if (status === undefined || status === null) {
      return this.translateService.instant(
        'goods.product_review.detail.values.unknown'
      );
    }

    // Convert backend status (0-4) to frontend status (1-5) for display
    const frontendStatus = StatusUtils.convertToFrontendStatus(status as any);
    const statusLabel = StatusUtils.getStatusLabel(frontendStatus);
    
    // Map status labels to i18n keys
    let i18nKey = '';
    if (frontendStatus === 2 || frontendStatus === 4) {
      i18nKey = 'goods.product_review.status.under_review';
    } else if (frontendStatus === 3) {
      i18nKey = 'goods.product_review.status.completed';
    } else if (frontendStatus === 1 || frontendStatus === 5) {
      i18nKey = 'goods.product_review.status.rejected';
    }
    
    return i18nKey
      ? this.translateService.instant(i18nKey)
      : statusLabel;
  }
}
